import { useCallback as useReactCallback } from 'react';
import { useScheduler } from '../SchedulerContext';
import { useStepNavigation } from '../StepNavigationContext';
import { clearDraftFromStorage } from '../utils/draftStorage';
import { callServerAction } from '@/util/functions/fetching/callServerAction';

interface UseStartOverOptions {
  /**
   * Step index to navigate to after clearing state
   * If undefined, no navigation occurs
   */
  navigateToStep?: number;
  
  /**
   * Whether to defer navigation with setTimeout(0)
   * Ensures state updates are processed before navigation
   * @default false
   */
  deferNavigation?: boolean;
  
  /**
   * Optional callback for component-specific cleanup
   * Called before clearing context states
   */
  onBeforeReset?: () => void;
  
  /**
   * Whether to wrap the returned function in useCallback
   * Useful for performance-critical components
   * @default false
   */
  useCallback?: boolean;
  
  /**
   * Whether to clear draft quote states
   * If true, deletes draft from server, clears localStorage and draft context states
   * @default true
   */
  clearDraft?: boolean;
}

/**
 * Hook to reset scheduler state and navigate to a specific step
 * 
 * Clears all scheduler context states except:
 * - contact (user information)
 * - selectedRole (user's role)
 * 
 * @example
 * // Simple usage - just clear state
 * const handleStartOver = useStartOver();
 * 
 * @example
 * // With navigation
 * const handleStartOver = useStartOver({ navigateToStep: 1 });
 * 
 * @example
 * // With component-specific cleanup
 * const handleStartOver = useStartOver({
 *   navigateToStep: 1,
 *   onBeforeReset: () => {
 *     setLocalState(null);
 *   },
 * });
 */
export function useStartOver(options: UseStartOverOptions = {}) {
  const {
    navigateToStep,
    deferNavigation = false,
    onBeforeReset,
    useCallback = false,
    clearDraft = true, // Default to true to prevent duplicate/empty quotes
  } = options;

  const {
    // Services
    setSelectedPrimaryService,
    setSelectedBundle,
    setSelectedAddons,
    setSelectedRoleIds,
    // Property
    setAddress,
    setStreetViewUrl,
    setZillowPropertyData,
    setContainingServiceAreaArray,
    setPropertyDetails,
    // AI & Recommendations
    setAiRecommendations,
    // Scheduling
    setSelectedDateTime,
    setSelectedInspectorForSlot,
    setSelectedSlotInspectorInfo,
    // Required Info
    setRequiredInfoValues,
    setEquipmentQuantities,
    setClosingDate,
    // Additional Contacts
    setAdditionalContacts,
    // Discounts
    setAppliedDiscountCode,
    setAppliedDiscountServiceIds,
    setPendingDiscountCode,
    dismissPrefillNotices,
    // Draft Quote
    draftQuoteId,
    setDraftQuoteId,
    setDraftQuoteSlug,
    setShouldResumeDraft,
    cancelDebounce,
    // Escape Hatch
    setEscapeHatchReason,
    // Property step progress
    resetPropertyStepProgress,
    jwtToken,
    companyId,
  } = useScheduler();

  const { setActiveStep } = useStepNavigation();

  const startOver = async () => {
    // 1. Call component-specific cleanup first
    if (onBeforeReset) {
      onBeforeReset();
    }

    // 1b. Cancel any pending debounced saves before clearing state
    cancelDebounce();

    // 2. Clear all scheduler state (except contact and selectedRole - these are preserved)
    setSelectedPrimaryService(null);
    setSelectedBundle(null);
    setSelectedAddons([]);
    setSelectedRoleIds([]);
    setAddress(null);
    setStreetViewUrl(null);
    setZillowPropertyData(null);
    setContainingServiceAreaArray(null);
    setPropertyDetails(null);
    setAiRecommendations(null);
    setSelectedDateTime(null);
    setSelectedInspectorForSlot({});
    setSelectedSlotInspectorInfo(null);
    setRequiredInfoValues([]);
    setEquipmentQuantities([]);
    setClosingDate('');
    setAdditionalContacts([]);
    setEscapeHatchReason(null);
    // The cart is empty again, so the Property step's internal pages have to be
    // walked afresh before the customer can leave it.
    resetPropertyStepProgress();
    // The cart is gone, so any discount applied to it goes too — including one
    // pre-applied by a scheduling link. Its banner is cleared with it so the
    // customer is not told a code is active when it is not. A code that was
    // only waiting for a qualifying service goes as well: the cart it was
    // waiting on no longer exists, and leaving it armed would apply a discount
    // the customer never asked for to whatever they build next.
    setAppliedDiscountCode(null);
    setAppliedDiscountServiceIds([]);
    setPendingDiscountCode(null);
    dismissPrefillNotices();

    // 3. Optionally clear draft quote states and delete from server
    if (clearDraft) {
      // Delete draft from server if it exists
      if (draftQuoteId && jwtToken && companyId) {
        try {
          await callServerAction({
            url: `quote/${draftQuoteId}`,
            method: 'delete',
            params: {
              _companyId: companyId,
            },
            headers: {
              'client-token': jwtToken,
            },
          });
          console.log('[Start Over] Deleted draft quote from server:', draftQuoteId);
        } catch (error) {
          console.error('[Start Over] Error deleting draft quote from server:', error);
          // Continue even if delete fails - might already be deleted
        }
      }
      
      // Clear localStorage
      clearDraftFromStorage();
      
      // Clear draft context states
      setDraftQuoteId(null);
      setDraftQuoteSlug(null);
      setShouldResumeDraft(false);
      console.log('[Start Over] Draft quote cleared from storage and context');
    }

    // 4. Optionally navigate to target step
    if (navigateToStep !== undefined) {
      if (deferNavigation) {
        // Defer navigation to ensure state updates are processed
        setTimeout(() => {
          setActiveStep(navigateToStep);
        }, 0);
      } else {
        setActiveStep(navigateToStep);
      }
    }
  };

  // Optionally wrap in useCallback for performance
  if (useCallback) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useReactCallback(startOver, [
      // All dependencies
      onBeforeReset,
      navigateToStep,
      deferNavigation,
      clearDraft,
      draftQuoteId,
      jwtToken,
      companyId,
      setSelectedPrimaryService,
      setSelectedBundle,
      setSelectedAddons,
      setSelectedRoleIds,
      cancelDebounce,
      setAddress,
      setStreetViewUrl,
      setZillowPropertyData,
      setContainingServiceAreaArray,
      setPropertyDetails,
      setAiRecommendations,
      setSelectedDateTime,
      setSelectedInspectorForSlot,
      setSelectedSlotInspectorInfo,
      setRequiredInfoValues,
      setEquipmentQuantities,
      setClosingDate,
      setAdditionalContacts,
      setAppliedDiscountCode,
      setAppliedDiscountServiceIds,
      setPendingDiscountCode,
      dismissPrefillNotices,
      setDraftQuoteId,
      setDraftQuoteSlug,
      setShouldResumeDraft,
      setEscapeHatchReason,
      setActiveStep,
    ]);
  }

  return startOver;
}










